import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { toast } from '@Shelly/shelly-design-system/index';

import { user_data } from 'src/stores';
import { forceLogout } from 'src/services/auth_v2';

/**
 * Profile socket events (FE-2216).
 *
 * After a verified account email change, the backend notifies all profile units
 * over the notifications socket as `{ event: "api2ui", ul: [<unit_id>], data }`.
 *
 * Captured payload shape:
 * - `{ changed: "PROFILE", id: "email", data: { email, old_email } }`
 *
 * Affected clients must clear local auth and require login with the new email.
 * Pending / failed / cancelled changes do not emit this event.
 */

export interface ProfileEmailChangedData {
	changed: 'PROFILE';
	id: 'email';
	data: {
		email: string;
		old_email: string;
	};
}

export const isProfileEmailChangedEvent = (data: unknown): data is ProfileEmailChangedData => {
	if (!data || typeof data !== 'object') return false;

	const payload = data as {
		changed?: unknown;
		id?: unknown;
		data?: { email?: unknown; old_email?: unknown };
	};

	return (
		payload.changed === 'PROFILE' &&
		payload.id === 'email' &&
		typeof payload.data?.email === 'string' &&
		typeof payload.data?.old_email === 'string'
	);
};

const showEmailChangedAlert = (): void => {
	const _format = get(format);
	toast.open({
		title: _format('email_changed', { default: 'Email changed' }),
		text: _format('email_changed_description', {
			default: 'Your account email was changed. Please log in with your new email.',
		}),
		type: 'danger',
		dangerWithProgress: true,
	});
};

/**
 * Invalidate the current session when this account's email was changed.
 * No-ops if the session is already on a different email (e.g. already logged out).
 *
 * Uses forceLogout → forceToLogin so web and native (iOS/Android) always clear
 * local/native auth and land on login, even if POST /auth/logout fails because
 * the server already invalidated the session.
 */
export const handleProfileEmailChanged = async (data: ProfileEmailChangedData): Promise<void> => {
	try {
		const currentEmail = get(user_data)?.email?.toLowerCase();
		const oldEmail = data.data.old_email.toLowerCase();

		if (!currentEmail || currentEmail !== oldEmail) {
			return;
		}

		await forceLogout(showEmailChangedAlert);
	} catch (error) {
		console.error('[profile] failed to handle email change socket event');
	}
};
